import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Library, Trash2, RefreshCw, Plus, Music, Film, Tv, Share2, Globe, Users, Upload } from 'lucide-react'
import { libraries, users } from '../api/client'
import { useAuthStore } from '../stores/auth'
import type { LibraryWithOwnership, LibraryShare } from '../types'

const libraryTypeIcons = {
  music: Music,
  movies: Film,
  tv: Tv,
}

function LibraryShareModal({
  library,
  onClose,
}: {
  library: LibraryWithOwnership
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [selectedUserId, setSelectedUserId] = useState('')
  const [accessLevel, setAccessLevel] = useState('read')

  const { data: allUsers } = useQuery({
    queryKey: ['users'],
    queryFn: users.list,
  })

  const { data: shares } = useQuery({
    queryKey: ['library-shares', library.id],
    queryFn: () => libraries.getShares(library.id),
  })

  const shareMutation = useMutation({
    mutationFn: () => libraries.share(library.id, selectedUserId, accessLevel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['library-shares', library.id] })
      setSelectedUserId('')
    },
  })

  const unshareMutation = useMutation({
    mutationFn: (userId: string) => libraries.unshare(library.id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['library-shares', library.id] })
    },
  })

  // Filter out users who already have access or are the owner
  const availableUsers = allUsers?.filter(
    (u) => u.id !== library.owner_id && !shares?.some((s) => s.user_id === u.id)
  )

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-dark-900 rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-semibold mb-4">Share "{library.name}"</h2>

        {/* Add share form */}
        <div className="flex gap-2 mb-6">
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="input flex-1"
          >
            <option value="">Select user...</option>
            {availableUsers?.map((user) => (
              <option key={user.id} value={user.id}>
                {user.username}
              </option>
            ))}
          </select>
          <select
            value={accessLevel}
            onChange={(e) => setAccessLevel(e.target.value)}
            className="input w-24"
          >
            <option value="read">Read</option>
            <option value="write">Write</option>
          </select>
          <button
            onClick={() => shareMutation.mutate()}
            disabled={!selectedUserId || shareMutation.isPending}
            className="btn btn-primary px-3"
          >
            Share
          </button>
        </div>

        {/* Current shares list */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-dark-400">Shared with:</h3>
          {shares?.length === 0 && (
            <p className="text-dark-500 text-sm">Not shared with anyone</p>
          )}
          {shares?.map((share: LibraryShare) => (
            <div
              key={share.user_id}
              className="flex items-center justify-between py-2 px-3 bg-dark-800 rounded"
            >
              <div>
                <span className="font-medium">{share.username}</span>
                <span className="ml-2 text-xs text-dark-400 capitalize">
                  {share.access_level}
                </span>
              </div>
              <button
                onClick={() => unshareMutation.mutate(share.user_id)}
                className="text-red-400 hover:text-red-300 text-sm"
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Libraries() {
  const queryClient = useQueryClient()
  const user = useAuthStore((state) => state.user)
  const [showAddForm, setShowAddForm] = useState(false)
  const [shareLibrary, setShareLibrary] = useState<LibraryWithOwnership | null>(null)
  const [newLibrary, setNewLibrary] = useState({
    name: '',
    path: '',
    library_type: 'music' as 'music' | 'movies' | 'tv',
  })

  const { data: libraryList, isLoading } = useQuery({
    queryKey: ['libraries'],
    queryFn: libraries.list,
  })

  const createMutation = useMutation({
    mutationFn: () => libraries.create(newLibrary.name, newLibrary.path, newLibrary.library_type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['libraries'] })
      setShowAddForm(false)
      setNewLibrary({ name: '', path: '', library_type: 'music' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: libraries.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['libraries'] })
    },
  })

  const scanMutation = useMutation({
    mutationFn: libraries.scan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['libraries'] })
    },
  })

  const toggleGlobalMutation = useMutation({
    mutationFn: ({ id, isGlobal }: { id: string; isGlobal: boolean }) =>
      libraries.setGlobal(id, isGlobal),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['libraries'] })
    },
  })

  const toggleUploadTargetMutation = useMutation({
    mutationFn: ({ id, isUploadTarget }: { id: string; isUploadTarget: boolean }) =>
      libraries.setUploadTarget(id, isUploadTarget),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['libraries'] })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate()
  }

  // Group libraries by access level
  // Note: A library can be both owned and global, so we filter by access_level AND is_mine
  const myLibraries = libraryList?.filter((lib) => lib.access_level === 'owner') || []
  const sharedLibraries = libraryList?.filter((lib) => lib.access_level === 'shared') || []
  // Only show global libraries that the user doesn't own (to avoid duplicates)
  const globalLibraries = libraryList?.filter((lib) => lib.access_level === 'global' && !lib.is_mine) || []

  const renderLibraryCard = (library: LibraryWithOwnership, showOwner = false) => {
    const Icon = libraryTypeIcons[library.library_type as keyof typeof libraryTypeIcons] || Library
    const canShare = library.access_level === 'owner'
    const canDelete = library.access_level === 'owner' || user?.is_admin
    const canToggleGlobal = user?.is_admin

    return (
      <div key={library.id} className="card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon size={20} className="sm:w-6 sm:h-6 text-primary-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-base sm:text-lg">{library.name}</h3>
                {library.is_global && (
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                    Global
                  </span>
                )}
                {library.is_upload_library && (
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                    Upload
                  </span>
                )}
                {library.is_upload_target && (
                  <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">
                    Upload Target
                  </span>
                )}
              </div>
              <p className="text-dark-400 text-sm truncate">{library.path}</p>
              <p className="text-dark-500 text-xs mt-1">
                Type: {library.library_type}
                {showOwner && library.owner_username && (
                  <span> • Owner: {library.owner_username}</span>
                )}
                {library.last_scanned && (
                  <span className="hidden sm:inline">
                    {' '}
                    • Last scanned: {new Date(library.last_scanned).toLocaleString()}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex gap-2 justify-end flex-wrap">
            {canShare && (
              <button
                onClick={() => setShareLibrary(library)}
                className="btn btn-secondary flex items-center gap-2"
                title="Share library"
              >
                <Share2 size={18} />
                <span className="hidden sm:inline">Share</span>
              </button>
            )}
            {canToggleGlobal && (
              <button
                onClick={() =>
                  toggleGlobalMutation.mutate({ id: library.id, isGlobal: !library.is_global })
                }
                disabled={toggleGlobalMutation.isPending}
                className={`btn flex items-center gap-2 ${
                  library.is_global
                    ? 'bg-blue-600/20 hover:bg-blue-600/30 text-blue-400'
                    : 'btn-secondary'
                }`}
                title={library.is_global ? 'Remove from global' : 'Make global'}
              >
                <Globe size={18} />
              </button>
            )}
            {canToggleGlobal && library.is_global && (
              <button
                onClick={() =>
                  toggleUploadTargetMutation.mutate({ id: library.id, isUploadTarget: !library.is_upload_target })
                }
                disabled={toggleUploadTargetMutation.isPending}
                className={`btn flex items-center gap-2 ${
                  library.is_upload_target
                    ? 'bg-purple-600/20 hover:bg-purple-600/30 text-purple-400'
                    : 'btn-secondary'
                }`}
                title={library.is_upload_target ? 'Remove as upload target' : 'Set as upload target'}
              >
                <Upload size={18} />
              </button>
            )}
            <button
              onClick={() => scanMutation.mutate(library.id)}
              disabled={scanMutation.isPending}
              className="btn btn-secondary flex items-center gap-2"
              title="Scan library"
            >
              <RefreshCw size={18} className={scanMutation.isPending ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Scan</span>
            </button>
            {canDelete && (
              <button
                onClick={() => {
                  if (confirm('Are you sure you want to delete this library?')) {
                    deleteMutation.mutate(library.id)
                  }
                }}
                disabled={deleteMutation.isPending}
                className="btn bg-red-600/20 hover:bg-red-600/30 text-red-400"
                title="Delete library"
              >
                <Trash2 size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Libraries</h1>
        {user?.is_admin && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="btn btn-primary flex items-center justify-center gap-2"
          >
            <Plus size={18} />
            Add Library
          </button>
        )}
      </div>

      {/* Add library form */}
      {showAddForm && user?.is_admin && (
        <form onSubmit={handleSubmit} className="card mb-4 sm:mb-6 space-y-4">
          <h2 className="text-lg sm:text-xl font-semibold">Add New Library</h2>

          {createMutation.error && (
            <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {(createMutation.error as any)?.response?.data?.error || 'Failed to create library'}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">Name</label>
              <input
                type="text"
                value={newLibrary.name}
                onChange={(e) => setNewLibrary({ ...newLibrary, name: e.target.value })}
                className="input"
                placeholder="My Music"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">Path</label>
              <input
                type="text"
                value={newLibrary.path}
                onChange={(e) => setNewLibrary({ ...newLibrary, path: e.target.value })}
                className="input"
                placeholder="/path/to/media"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">Type</label>
              <select
                value={newLibrary.library_type}
                onChange={(e) =>
                  setNewLibrary({
                    ...newLibrary,
                    library_type: e.target.value as 'music' | 'movies' | 'tv',
                  })
                }
                className="input"
              >
                <option value="music">Music</option>
                <option value="movies">Movies</option>
                <option value="tv">TV Shows</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button type="submit" disabled={createMutation.isPending} className="btn btn-primary">
              {createMutation.isPending ? 'Creating...' : 'Create Library'}
            </button>
            <button type="button" onClick={() => setShowAddForm(false)} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Library sections */}
      {isLoading ? (
        <div className="text-dark-400">Loading...</div>
      ) : libraryList && libraryList.length > 0 ? (
        <div className="space-y-8">
          {/* My Libraries */}
          {myLibraries.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Library size={20} className="text-primary-400" />
                <h2 className="text-xl font-semibold">My Libraries</h2>
                <span className="text-dark-500 text-sm">({myLibraries.length})</span>
              </div>
              <div className="grid gap-3 sm:gap-4">{myLibraries.map((lib) => renderLibraryCard(lib))}</div>
            </section>
          )}

          {/* Shared With Me */}
          {sharedLibraries.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Users size={20} className="text-green-400" />
                <h2 className="text-xl font-semibold">Shared With Me</h2>
                <span className="text-dark-500 text-sm">({sharedLibraries.length})</span>
              </div>
              <div className="grid gap-3 sm:gap-4">
                {sharedLibraries.map((lib) => renderLibraryCard(lib, true))}
              </div>
            </section>
          )}

          {/* Global Catalog */}
          {globalLibraries.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Globe size={20} className="text-blue-400" />
                <h2 className="text-xl font-semibold">Global Catalog</h2>
                <span className="text-dark-500 text-sm">({globalLibraries.length})</span>
              </div>
              <div className="grid gap-3 sm:gap-4">
                {globalLibraries.map((lib) => renderLibraryCard(lib, true))}
              </div>
            </section>
          )}
        </div>
      ) : (
        <div className="card text-center py-12">
          <Library size={48} className="text-dark-500 mx-auto mb-4" />
          <p className="text-dark-400 mb-2">No libraries available</p>
          <p className="text-dark-500 text-sm">
            {user?.is_admin
              ? 'Add a library to start scanning your media'
              : 'Ask an admin to add a library or share one with you'}
          </p>
        </div>
      )}

      {/* Share Modal */}
      {shareLibrary && (
        <LibraryShareModal library={shareLibrary} onClose={() => setShareLibrary(null)} />
      )}
    </div>
  )
}
